import React, { useState } from 'react';
import {
    Modal,
    Box,
    Button,
    Typography,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    LinearProgress,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Paper,
} from '@mui/material';
import Papa from 'papaparse'; // For CSV export
import * as XLSX from 'xlsx';
import { useSelector } from "react-redux";
import Loader from "../Inputs/Loader/Loader";

// Define styles for the modal
const styles = {
    modal: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    box: {
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '8px',
        maxHeight: '500px',
        boxShadow: '0px 3px 6px rgba(0, 0, 0, 0.1)',
        overflow: 'auto'
    },
};

const ExportModal = ({
                         open,
                         onClose,
                         isMovements,
                         progress,
                         generatedFiles,
                         fileGenerationLoading,
                         handleDownloadAll = () => {},
                         dataType = 'movements',
                         fileName,
                         onCancelExport,
                         isExporting,
                     }) => {
    const [fileType, setFileType] = useState('CSV'); // Default to CSV
    const [range, setRange] = useState('today');       // Default to today
    // Local state to record cancelled exports
    const [cancelledExports, setCancelledExports] = useState([]);
    // State for error messages
    const [exportError, setExportError] = useState('');
    const [filesError, setFilesError] = useState('');
    const userStore = useSelector((state) => state.user);

    const handleFileTypeChange = (event) => {
        setFileType(event.target.value);
    };

    const handleRangeChange = (event) => {
        setRange(event.target.value);
    };

    const handleExport = async () => {
        setExportError('');
        try {
            if (dataType === 'trips') {
                await handleDownloadAll(fileType);
            } else if (dataType === 'movements') {
                await handleDownloadAll(fileType);
            } else if (dataType === 'cartsStatus') {
                await handleDownloadAll(fileType, fileName);
            } else if (dataType === 'equipment') {
                await handleDownloadAll(fileType);
            } else if (dataType === 'tags') {
                await handleDownloadAll(fileType);
            }
        } catch (error) {
            console.error("Export error:", error);
            setExportError("Error generating file. Please try again.");
            throw error;
        }
    };

    // When canceling an export, call the cancel handler and update the local cancelledExports state.
    const handleCancelExport = () => {
        onCancelExport();
        const newRecord = {
            startTime: new Date().toISOString(),
            fileName: fileName || `export_${Date.now()}.${fileType.toLowerCase()}`,
            fileType: fileType,
            status: 'Cancelled',
        };

        setCancelledExports(prev => {
            // Check if there's already a cancelled record for the current export parameters.
            const existingIndex = prev.findIndex(
                record => record.fileType === fileType && record.status === 'Cancelled'
            );
            if (existingIndex !== -1) {
                // Update that record's timestamp (and file name) instead of adding a new record.
                const updatedRecord = { ...prev[existingIndex], ...newRecord };
                const newArr = [...prev];
                newArr[existingIndex] = updatedRecord;
                return newArr;
            } else {
                return [newRecord, ...prev];
            }
        });
    };

    const handleRegenerate = async (exportRecord) => {
        // Optionally, update export parameters if needed.
        setFileType(exportRecord.fileType);
        setExportError('');
        try {
            // Await the export call so that we know it succeeded.
            await handleExport();
            // Remove the cancelled record so it doesn't appear in the list.
            setCancelledExports((prev) =>
                prev.filter((rec) => rec.startTime !== exportRecord.startTime)
            );
        } catch (error) {
            setExportError("Regeneration failed. Please try again.");
        }
    };

    // Deduplicate generatedFiles by fileUrl
    const uniqueGeneratedFiles = Array.from(
        new Map((generatedFiles || []).map(file => [file.fileUrl, file])).values()
    );

    // Convert deduplicated generatedFiles to a standard format
    const completedExports = uniqueGeneratedFiles.map(file => ({
        startTime: file.createdAt,
        fileName: file.fileName,
        fileType: file.fileName.split('.').pop().toUpperCase(),
        status: 'Completed',
        fileUrl: file.fileUrl,
    }));

    // Merge cancelled exports with completed exports for display
    const allExports = [...cancelledExports, ...completedExports];

    return (
        <Modal open={open} onClose={onClose} style={styles.modal}>
            <Box style={styles.box}>
                <Typography variant="h6" sx={{ color: "#1B3665", fontWeight: 600 }}>
                    Export Data
                </Typography>

                <Box
                    sx={{
                        display: "flex",
                        gap: '10px',
                        alignItems: "flex-end",
                        justifyContent: 'space-between',
                        marginTop: '16px'
                    }}
                >
                    {fileName && (
                        <Typography variant="body1" sx={{ marginBottom: '8px' }}>
                            {fileName}
                        </Typography>
                    )}
                    <FormControl sx={{ minWidth: 300 }} margin="normal" size="small">
                        <InputLabel>File Type</InputLabel>
                        <Select
                            value={fileType}
                            onChange={handleFileTypeChange}
                            variant="standard"
                            label="File Type"
                        >
                            <MenuItem value="CSV">CSV</MenuItem>
                            <MenuItem value="Excel">Excel</MenuItem>
                        </Select>
                    </FormControl>

                    <Button
                        sx={{
                            marginLeft: !userStore?.user?.roles?.includes('admin') ? "auto" : "0",
                            transform: 'translateY(-7px)'
                        }}
                        size="small"
                        variant="contained"
                        color="primary"
                        onClick={handleExport}
                        disabled={fileGenerationLoading || isExporting}
                    >
                        EXPORT
                    </Button>
                </Box>

                {(fileGenerationLoading || isExporting) && (
                    <Box sx={{ marginTop: '10px' }}>
                        <Typography variant="body2">Generating file...</Typography>
                        <Loader sx={{ height: '50px' }} />
                        <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
                            <Button
                                variant="outlined"
                                color="secondary"
                                size="small"
                                onClick={handleCancelExport}
                            >
                                Cancel Export
                            </Button>
                        </Box>
                    </Box>
                )}

                {isMovements && progress > 0 && (
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="body2">
                            Uploading… {progress}%
                        </Typography>
                        <LinearProgress variant="determinate" value={progress} />
                    </Box>
                )}

                {exportError && (
                    <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                        {exportError}
                    </Typography>
                )}

                {/* Exports List Table */}
                {dataType !== 'cartsStatus' && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                      Exports List
                    </Typography>
                    {allExports.length === 0 ? (
                      <Typography variant="body2">No exports available.</Typography>
                    ) : (
                      <Paper variant="outlined" sx={{ maxHeight: '300px', overflowY: 'auto' }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Start Time</TableCell>
                              <TableCell>File Name</TableCell>
                              <TableCell>File Type</TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell>Action</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {allExports.map((row, index) => (
                              <TableRow key={index}>
                                <TableCell>{new Date(row.startTime).toLocaleString()}</TableCell>
                                <TableCell>{row.fileName}</TableCell>
                                <TableCell>{row.fileType}</TableCell>
                                <TableCell>{row.status}</TableCell>
                                <TableCell>
                                  {row.status === 'Cancelled' ? (
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      onClick={() => handleRegenerate(row)}
                                    >
                                      Regenerate
                                    </Button>
                                  ) : (
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      onClick={() => window.open(row.fileUrl, '_blank')}
                                    >
                                      Download
                                    </Button>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Paper>
                    )}
                </Box>)}


                {filesError && dataType !== 'cartsStatus' && (
                    <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                        {filesError}
                    </Typography>
                )}
            </Box>
        </Modal>
    );
};

export default ExportModal;
